import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { EASE, DUR } from '../utils/motion';

/**
 * Route transition.
 *
 * Navigating between /, /services and /appointment was an instant cut — the new
 * page simply appeared. labeau_landing eases between views; this does the same
 * with a short lift-and-fade on entry.
 *
 * Entry only, deliberately. A proper exit animation means holding the outgoing
 * route mounted while the incoming one renders, which needs the old and new
 * elements on screen together — that fights `ScrollToTop` (the page would jump
 * while the outgoing view is still visible) and doubles the mounted tree on a
 * page that already lazy-loads seven sections. The asymmetry is not noticeable;
 * the dead-cut was.
 *
 * `key={pathname}` guarantees React tears down and rebuilds the subtree on every
 * navigation, so the animation restarts even when routes share a component.
 *
 * `prefers-reduced-motion` is handled globally in index.css, which collapses
 * this to 0.01ms.
 */
const PageTransition = ({ children }) => {
    const { pathname } = useLocation();

    return (
        <Box
            key={pathname}
            sx={{
                animation: `ts-page-in ${DUR.page}s ${EASE} both`,
                // Contain the transform so the animating page can't widen the
                // document and flash a horizontal scrollbar mid-transition.
                overflowX: 'clip',
            }}
        >
            {children}
        </Box>
    );
};

export default PageTransition;
